import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule, isDevMode } from '@angular/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { IonicModule, IonicRouteStrategy, Platform, ToastController } from '@ionic/angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgLetModule } from 'ng-let';
import {
    AdminModule,
    ArmyBuilderConfig,
    ArmyBuilderSharedModule,
    ArmyBuilderStoreModule,
    HttpClientWithInFlightCacheModule,
    LanguageModule,
    MenuModule,
    LoginComponent,
    PageLayoutModule,
    pageTransition,
    SafeHtmlModule,
    SettingsModule,
    UserModule,
    UserService,
    DEFAULT_SETTINGS_VALUE,
    ParseIconsConfig,
    getTranslateModuleConfig,
    EntityPreloadModule,
    TranslationPreloadModule,
    GAME_ID,
    DataSyncModule,
    SocialSigninModule,
    SupportModule,
    SupportService,
    SettingsService,
    ImpersonationBarComponent,
    BillingSimulationBarComponent,
    BillingIssueWarningComponent,
    FeatureFlagDirective
} from 'army-builder-shared';
import { Insomnia } from '@awesome-cordova-plugins/insomnia/ngx';
import { provideAuth, getAuth, initializeAuth, indexedDBLocalPersistence } from '@angular/fire/auth';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { Capacitor } from '@capacitor/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IonicStorageModule } from '@ionic/storage-angular';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FalloutConfig } from './config';
import { FalloutSettingsModule } from './settings/settings.module';
import { FalloutUserService } from './settings/user/user.service';
// import { falloutAppInitializerFactory } from './fallout-app.init';
import { ContentPage } from './home/content.page';
import { FalloutSubscriptionPageModule } from './subscription-page/subscription-page.module';
import { MyHammerConfig } from './global/hammer.config';
import { HomePage } from './home/home.page';
import { EQUIPMENT_TYPES } from './fww/global/models';
import { debounceTime, filter, withLatestFrom } from 'rxjs';
@NgModule({
    declarations: [AppComponent, HomePage, ContentPage],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        IonicModule.forRoot({
            mode: 'md',
            navAnimation: pageTransition,
            innerHTMLTemplatesEnabled: true
        }),
        AppRoutingModule,
        ArmyBuilderStoreModule,
        ArmyBuilderSharedModule,
        NgLetModule,
        AdminModule,
        PageLayoutModule,
        LanguageModule,
        HttpClientWithInFlightCacheModule,
        SafeHtmlModule,
        UserModule,
        MenuModule,
        SettingsModule,
        IonicStorageModule.forRoot(),
        TranslateModule.forChild(getTranslateModuleConfig('global')),
        FalloutSettingsModule,
        FalloutSubscriptionPageModule,
        HammerModule,
        EntityPreloadModule,
        TranslationPreloadModule,
        DataSyncModule,
        SocialSigninModule,
        SupportModule,
        ImpersonationBarComponent,
        BillingSimulationBarComponent,
        BillingIssueWarningComponent,
        LoginComponent,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode() && !Capacitor.isNativePlatform(),
            registrationStrategy: 'registerWhenStable:30000'
        }),
        FeatureFlagDirective
    ],
    providers: [
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => {
            if (Capacitor.isNativePlatform()) {
                return initializeAuth(getApp(), {
                    persistence: indexedDBLocalPersistence
                });
            } else {
                return getAuth();
            }
        }),

        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: ArmyBuilderConfig, useClass: FalloutConfig },
        FalloutConfig,
        Insomnia,
        {
            provide: UserService,
            useExisting: FalloutUserService
        },
        // {
        //     provide: APP_INITIALIZER,
        //     useFactory: falloutAppInitializerFactory,
        //     deps: [TranslateService, Injector],
        //     multi: true
        // },
        {
            provide: DEFAULT_SETTINGS_VALUE,
            useValue: {
                itemFilters: { item: { type: EQUIPMENT_TYPES } },
                itemSort: {
                    item: {}
                },
                disableGrouping: {
                    item: {}
                },
                showIllegalItems: {
                    item: {}
                }
            }
        },
        {
            provide: ParseIconsConfig,
            useValue: {
                iconsFolder: 'assets/images/icons',
                glyphs: {
                    // FWW2E's rules express an enemy's Danger Rating as a run of skulls, inline in
                    // prose — "Place one 💀💀💀💀 enemy in Position 1". Content is authored with the
                    // character so it stays legible in the admin editor and matches the rulebook,
                    // and is swapped here for the same markup `fww2e-danger-rating` renders, so the
                    // artwork and its size live in one place (`.dangerSkull` in fww2e.scss) rather
                    // than being pasted as an image tag at every occurrence.
                    //
                    // Scoped to fww2e: the other Fallout games share this provider and have no such
                    // convention, so a bare skull in their content stays a skull.
                    fww2e: {
                        '💀': {
                            html: '<span class="dangerSkull"></span>',
                            groupClass: 'dangerRating',
                            groupLabel: 'Danger rating'
                        },
                        // Reuses the armour-radiation icon artwork (a plain radiation trefoil, not
                        // anything armour-specific) rather than a second asset for the same symbol.
                        '☢': {
                            html: '<span class="fww2eIcon fww2eIcon--armour-radiation"></span>',
                            groupClass: 'radHazardIcon',
                            groupLabel: 'Radiation hazard'
                        },
                        // The rulebook marks each enemy Behavior action with one or two interlocking-cog symbols,
                        // meaning "the maximum number of times a model can make that action in a single activation"
                        // (Ch6, Enemy Behavior) — not action points. Authored as the gear character so the stored
                        // content stays readable in the admin editor; `replaceGlyphRuns` groups a run so it is
                        // announced once with its count rather than twice over.
                        '⚙': {
                            html: '<span class="fww2eIcon fww2eIcon--enemy-action"></span>',
                            groupClass: 'actionLimit',
                            groupLabel: 'Maximum uses per activation'
                        }
                    }
                },
                icons: {
                    // Monochrome SVGs rendered via CSS mask-image; armour icons use currentColor,
                    // dice use per-die colours so black/yellow/green are always distinct.
                    fww2e: {
                        'armour-physical': {
                            html: '<span class="fww2eIcon fww2eIcon--armour-physical {sizeClass}" role="img" aria-label="{alt}"></span>'
                        },
                        'armour-energy': {
                            html: '<span class="fww2eIcon fww2eIcon--armour-energy {sizeClass}" role="img" aria-label="{alt}"></span>'
                        },
                        'armour-radiation': {
                            html: '<span class="fww2eIcon fww2eIcon--armour-radiation {sizeClass}" role="img" aria-label="{alt}"></span>'
                        },
                        'caps-white': {
                            html: '<span class="fww2eIcon fww2eIcon--caps-white {sizeClass}" role="img" aria-label="{alt}"></span>'
                        },
                        movement: { html: '<span class="fww2eIcon fww2eIcon--movement {sizeClass}" role="img" aria-label="{alt}"></span>' }
                    }
                }
            }
        },
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        },
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: GAME_ID,
            useValue: 'global'
        }
    ]
})
export class AppModule {
    constructor(
        public platform: Platform,
        private insomnia: Insomnia,
        private supportService: SupportService,
        private settingsService: SettingsService,
        private toastController: ToastController,
        private router: Router
    ) {
        this.platform.ready().then(() => {
            this.insomnia.keepAwake().then(
                () => console.log('insomnia success'),
                (e) => console.log('insomnia error', e)
            );

            this.settingsService.loggedIn$
                .pipe(
                    filter((loggedIn) => loggedIn),
                    debounceTime(500),
                    withLatestFrom(this.supportService.unreadCount$)
                )
                .subscribe(async ([_, unreadCount]) => {
                    if (unreadCount > 0) {
                        const toast = await this.toastController.create({
                            message: `You have ${unreadCount} support tickets with new updates.`,
                            position: 'top',
                            color: 'warning',
                            layout: 'stacked',
                            buttons: [
                                {
                                    text: 'Go to support tickets',
                                    handler: () => {
                                        this.toastController.dismiss();
                                        this.router.navigateByUrl('/support');
                                    }
                                },
                                {
                                    text: 'Dismiss',
                                    handler: () => this.toastController.dismiss()
                                }
                            ]
                        });
                        toast.present();
                    }
                });
        });
    }
}
